import './App.css';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/browser';
import ls from 'local-storage';
import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import AppWrapper from '@/AppWrapper';
import PermissionLanding from '@/components/permission-landing';
import PermissionRoute from '@/components/permission-route';
import PrivateRoute from '@/components/private-route';
import firebaseApp from '@/config/firebase.config';
// import Notifications from '@/pages/Notifications';
import Error404 from '@/pages/404';
import ActionPage from '@/pages/Actions';
import Login from '@/pages/Auth/Login';
import BrandEditor from '@/pages/brands/editor';
// import BrandDashboard from '@/pages/BrandDashboard';
import BrandManager from '@/pages/brands/manager';
import BusinessClaims from '@/pages/businesses/claims';
// import BrandClaimManager from '@/pages/BrandClaimManager';
import CigarDashboard from '@/pages/cigars/dashboard';
import CigarEditor from '@/pages/cigars/editor';
import CigarManager from '@/pages/cigars/manager';
import CigarMatchManager from '@/pages/cigars/matches';
import CigarBandRecognition from '@/pages/cigars/recognition/manager';
import VitolaManager from '@/pages/cigars/vitolas/manager';
import DrinkManager from '@/pages/drink_pairings/manager';
import EventManager from '@/pages/events/manager';
import GroupManager from '@/pages/groups/manager';
import Settings from '@/pages/settings';
import Roles from '@/pages/settings/roles';
import RoleDetails from '@/pages/settings/roles/editor';
import Users from '@/pages/settings/users';
import AbandonedCarts from '@/pages/shop/carts/abandoned';
import PaymentGatewayConfig from '@/pages/shop/config/payment_gateways';
import ShippingConfig from '@/pages/shop/config/shipping';
import TaxConfig from '@/pages/shop/config/taxes';
import Discounts from '@/pages/shop/discounts';
import ProductEditor from '@/pages/shop/editor';
// import VenueClaimManager from '@/pages/VenueClaimManager';
// import ShopDashboard from '@/pages/ShopDashboard';
import ProductManager from '@/pages/shop/manager';
import OrderManager from '@/pages/shop/orders';
import GrowthDashboard from '@/pages/tools/growth';
import UserEditor from '@/pages/users/editor';
import UserManager from '@/pages/users/manager';
import VenueEditor from '@/pages/venues/editor';
// import VenueDashboard from '@/pages/VenueDashboard';
import VenueManager from '@/pages/venues/manager';
import PendingVenueManager from '@/pages/venues/pending';
import { UserStore } from '@/stores';

import { getApiErrorMessage, getCurrentAdminAccess } from './api/admin-access';
import store from './store';

const theme = createTheme({
  palette: {
    primary: {
      main: '#212121',
    },
    secondary: {
      // light: will be calculated from palette.secondary.main,
      main: 'rgb(211, 169, 102)',
      // dark: will be calculated from palette.secondary.main,
      contrastText: '#212121',
    },
    // @ts-ignore - this is okay, it creates a new palette named "danger"
    danger: {
      main: '#ff4700',
    },
    error: {
      main: '#ff4700',
    },
  },
});

function App() {
  useEffect(() => {
    const unsubscribe = firebaseApp.auth().onAuthStateChanged(async (user) => {
      if (user) {
        UserStore.update((s) => {
          s.firebaseUser = user;
          s.authenticated = true;
          s.access = null;
          s.loadingAccess = true;
          s.accessError = null;
        });
        console.log('User is logged in!');
        Sentry.setUser({
          email: user.email,
        });
        try {
          const token = await user.getIdToken();
          ls('boxpressd_admin_token_id', token);
          const access = await getCurrentAdminAccess();
          UserStore.update((s) => {
            s.access = access;
            s.loadingAccess = false;
          });
        } catch (error) {
          UserStore.update((s) => {
            s.access = null;
            s.loadingAccess = false;
            s.accessError = getApiErrorMessage(error);
          });
        }
      } else {
        UserStore.update((s) => {
          s.firebaseUser = null;
          s.authenticated = false;
          s.access = null;
          s.loadingAccess = false;
          s.accessError = null;
        });
      }
    });
    return () => unsubscribe();
  }, []);

  const protectedPage = (
    permissionKey: string | string[],
    page: React.ReactNode
  ) => (
    <PrivateRoute>
      <PermissionRoute permissionKey={permissionKey}>{page}</PermissionRoute>
    </PrivateRoute>
  );

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <BrowserRouter basename="/">
          <Routes>
            {/* FIXME Enable the user to set the default route? Or reload to last used route? */}
            <Route
              path={`${process.env.PUBLIC_URL}/`}
              element={<PermissionLanding />}
            />

            {/* Public routes */}
            <Route
              path={`${process.env.PUBLIC_URL}/login`}
              element={<Login />}
            />
            {/* <Route */}
            {/*  path={`${process.env.PUBLIC_URL}/pages/forget-password`} */}
            {/*  element={<ForgetPassword />} */}
            {/* /> */}
            {/* <Route */}
            {/*  path={`${process.env.PUBLIC_URL}/pages/reset-password`} */}
            {/*  element={<ResetPassword />} */}
            {/* /> */}

            {/* Private routes */}
            <Route element={<AppWrapper />}>
              <Route
                path={`${process.env.PUBLIC_URL}/tools/growth`}
                element={protectedPage('tools', <GrowthDashboard />)}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/cigars/dashboard`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <CigarDashboard /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route
                path={`${process.env.PUBLIC_URL}/cigars/dashboard`}
                element={protectedPage('cigars', <CigarDashboard />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/cigars/manager`}
                element={protectedPage('cigars', <CigarManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/cigars/vitolas/manager`}
                element={protectedPage('cigars', <VitolaManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/cigars/editor/:id`}
                element={protectedPage('cigars', <CigarEditor />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/targets/manager`}
                element={protectedPage('cigars', <CigarBandRecognition />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/drink-pairings/manager`}
                element={protectedPage('drink_pairings', <DrinkManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/cigars/matches`}
                element={protectedPage('cigars', <CigarMatchManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/businesses/claims`}
                element={protectedPage(
                  'BusinessClaimsApproval',
                  <BusinessClaims />
                )}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/brands/dashboard`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <BrandDashboard /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route
                path={`${process.env.PUBLIC_URL}/brands/manager`}
                element={protectedPage(
                  ['cigars', 'businesses'],
                  // @ts-ignore FIXME Convert this to a functional component
                  <BrandManager />
                )}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/brands/editor/:id`}
                element={protectedPage(
                  ['cigars', 'businesses'],
                  <BrandEditor />
                )}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/brands/claims`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <BrandClaimManager /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route
                path={`${process.env.PUBLIC_URL}/groups/manager`}
                element={protectedPage(
                  'groups',
                  // @ts-ignore FIXME Convert this to a functional component
                  <GroupManager />
                )}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/venues/dashboard`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <VenueDashboard /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route
                path={`${process.env.PUBLIC_URL}/venues/manager`}
                element={protectedPage('businesses', <VenueManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/venues/editor/:id`}
                element={protectedPage('businesses', <VenueEditor />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/venues/pending`}
                element={protectedPage('businesses', <PendingVenueManager />)}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/venues/claims`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <VenueClaimManager /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/shop/dashboard`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <ShopDashboard /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route
                path={`${process.env.PUBLIC_URL}/shop/manager`}
                element={protectedPage('shop', <ProductManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/editor/:id`}
                element={protectedPage('shop', <ProductEditor />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/carts/abandoned`}
                element={protectedPage('shop', <AbandonedCarts />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/orders`}
                element={protectedPage('shop', <OrderManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/discounts`}
                element={protectedPage('shop', <Discounts />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/config/payment-gateways`}
                element={protectedPage('shop', <PaymentGatewayConfig />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/config/taxes`}
                element={protectedPage('shop', <TaxConfig />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/shop/config/shipping`}
                element={protectedPage('shop', <ShippingConfig />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/events/manager`}
                element={protectedPage(
                  'events',
                  // @ts-ignore FIXME Convert this to a functional component
                  <EventManager />
                )}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/actions`}
                element={protectedPage('tools', <ActionPage />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/users/manager`}
                element={protectedPage('users', <UserManager />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/users/editor/:id`}
                element={protectedPage('users', <UserEditor />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/settings`}
                element={protectedPage('settings', <Settings />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/settings/roles`}
                element={protectedPage('settings', <Roles />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/settings/roles/:id`}
                element={protectedPage('settings', <RoleDetails />)}
              />
              <Route
                path={`${process.env.PUBLIC_URL}/settings/users`}
                element={protectedPage('settings', <Users />)}
              />
              {/*  <Route */}
              {/*    path={`${process.env.PUBLIC_URL}/notifications`} */}
              {/*    element={ */}
              {/*      <PrivateRoute> */}
              {/*        <Notifications /> */}
              {/*      </PrivateRoute> */}
              {/*    } */}
              {/*  /> */}
              <Route path="*" element={<Error404 />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
